import * as authActions from "./actions";
import { AuthAPI } from "../../API";
import { toast } from 'react-toastify';
import LoginContent from "../../Languages/Login/content.json";
import i18n from "../../i18n";
import country from "../../config/countryConfig";

// Reset the session language to the account default returned by the backend
// (users.language). Every fresh login lands here, so whatever the toggle held
// before login is overwritten — both Redux (via languageChangeNavbar) and
// localStorage('userLanguagePreference') (the action creator writes it) and the
// i18next page chrome (i18n.changeLanguage). Legacy rows with NULL/empty
// language fall back to the portal's primary supported language. `??` (not `||`)
// so a deliberately-stored value is never clobbered, only true null/undefined.
const applyAccountLanguage = (dispatch, rawLanguage) => {
  const supported = Array.isArray(country.supportedLanguages) && country.supportedLanguages.length
    ? country.supportedLanguages
    : ['en'];
  const fallback = supported[0] ?? 'en';
  const candidate = rawLanguage ?? '';
  const lang = supported.includes(candidate) ? candidate : fallback;
  dispatch(languageChangeNavbar(lang));
  i18n.changeLanguage(lang);
};

// Maps a backend login-failure message to a translated toast and shows it.
// Fired directly from authLogin's catch — a reliable per-attempt signal,
// unlike inferring failures from Redux state transitions (a repeated
// identical error produces no state change for a useEffect to observe).
const showLoginErrorToast = (errorMessage, language) => {
  const section = language === 'jp'
    ? LoginContent.LoginJapanese
    : language === 'hi'
      ? LoginContent.LoginHindi
      : LoginContent.LoginEnglish;
  const panel = (section && section.panel) || {};
  if (errorMessage === 'User Not found.') {
    toast.error(panel.errorUserNotFound || 'User not registered with this email');
  } else if (errorMessage === 'Invalid Password!') {
    toast.error(panel.errorWrongPassword || 'Incorrect password');
  } else {
    toast.error(errorMessage || 'Login failed');
  }
};

export const authStart = () => ({
  type: authActions.AUTH_START,
});

export const authSuccess = (user) => ({
  type: authActions.AUTH_SUCCESS,
  user,
});

export const authSuccessLastStep = (user) => ({
    type: authActions.AUTH_SUCCESS_LAST_STEP,
    user,
});

export const authSuccessMobileStep = (user) => ({
    type: authActions.AUTH_SUCCESS_MOBILE_STEP,
    user,
});

export const authRegisterSuccess = (user) => ({
    type: authActions.AUTH_REGISTRATION_SUCCESS,
    user,
});

export const languageChange = (user) => {
  console.log([
    authActions.LANGUAGE_CHANGE,
    user
  ]);
  return {
    type: authActions.LANGUAGE_CHANGE,
    user,
  }
};

export const languageChangeNavbar = (user) => {
    // Persist the choice so it survives logout (logout does localStorage.clear()
    // + a full page reload, discarding Redux state) and hard refresh. On the
    // next boot, index.tsx's detectInitialLanguage() reads this key — same
    // 'userLanguagePreference' key the Login/Signup dropdowns already write.
    try { localStorage.setItem('userLanguagePreference', user); } catch (e) { /* storage unavailable — non-fatal */ }
    return {
        type: authActions.LANGUAGE_CHANGE_NAVBAR,
        user,
    }
};

export const basicProfileCompleted = (user) => ({
    type: authActions.AUTH_BASIC_PROFILE_SUCCESS,
    user,
});

export const authFail = (error) => (dispatch) => {
  dispatch({
    type: authActions.AUTH_FAIL,
    error,
  });
};


export const authLogin =
  (username, password, registerType, language, history, verisoulSessionId) => async (dispatch) => {
    try {
      dispatch(authStart());
      const user = await AuthAPI.login(username, password, registerType, language, verisoulSessionId);
        // Every successful login resets the session to the account default
        // (users.language), overriding whatever the toggle held pre-login.
        // Done before routing so the destination renders in the right language.
        applyAccountLanguage(dispatch, user.language);
        if(user.phoneNumberConfirmed === false) {
            dispatch(authSuccessMobileStep(user))
            return history.push({
                pathname: '/auth/verify-mobile',
                state: { userId: user.id },
            });
        } else if(user.emailConfirmed === true && user.basicProfile) {
          dispatch(authSuccess(user));
          if (user.role === 'panelist') {
              const returnUrl = history.location?.state?.returnUrl;
              // returnUrl is a bare path like /surveys/516477/uuid — HashRouter
              // handles the hash prefix automatically when using history.push.
              history.push(returnUrl || '/panelist/dashboard');
          } else if (user.role === 'pm') {
              history.push("/pm/dashboard");
          } else if (user.role === 'sub-admin') {
              history.push("/sub-admin/redemption");
          } else if (user.role === 'finance') {
              history.push("/finance/approveRedeemptionRequests");
          } else {
              history.push("/admin/dashboard-admin");
          }
      } else if(user.basicProfile === null && user.emailConfirmed === true){
          dispatch(authSuccessLastStep(user));
          history.push("/auth/basic-profile");
      } else {
          dispatch(authRegisterSuccess(user));
          history.push("/verify-screen");
      }
    } catch (error) {
      dispatch(authFail(error.message));
      // Show the failure as a toastr immediately — the catch is the
      // reliable per-attempt hook (state-diff effects miss repeated
      // identical errors).
      showLoginErrorToast(error.message, language);
    }
  };

export const authBasicProfile =
    (history, id, role, apiToken, loginRewardType) => async (dispatch) => {
        try {
            dispatch(authStart());
            let obj = { id, role, apiToken, loginRewardType };
            dispatch(basicProfileCompleted(obj));
            if (obj.role === 'panelist') {
                history.push("/panelist/dashboard");
            } else if (obj.role === 'pm') {
                history.push("/pm/dashboard");
            } else if (obj.role === 'sub-admin') {
               history.push("/sub-admin/redemption");
            } else if (obj.role === 'finance') {
                history.push("/finance/approveRedeemptionRequests");
            } else {
                history.push("/admin/dashboard-admin");
            }
        } catch (error) {
            dispatch(authFail(error.message));
        }
    };

export const authRegistration =
    (data, history) => async (dispatch) => {
      try {
        dispatch(authStart());
        const user = await AuthAPI.registration(data);
        dispatch(authSuccessLastStep(user));
              return history.push({
                  pathname: '/auth/basic-profile',
                  state: { userId: user.userId, role: user.role, apiToken: user.apiToken },
              });
      } catch (error) {
        dispatch(authFail(error.message));
        // Mirror authLogin's pattern — toast directly from catch is the reliable per-attempt hook.
        toast.error(error.message || 'Signup failed');
      }
    };


export const languageChangeOption =
    (language) => async (dispatch) => {
        console.log('language--->>>>>', language)
            dispatch(languageChange(language));

    };
